import apiService from './api';
import type { ApiResponse } from '../types';

export interface PayCyclePeriod {
  startDate: string;
  endDate: string;
  payFrequency: 'weekly' | 'fortnightly';
  payCycleStartDay: number;
  fortnightlyReferenceDate: string | null;
}

export interface PayCycleTimesheetEntry {
  id: number;
  date: string;
  clockInTime: string;
  clockOutTime: string | null;
  originalClockInTime: string | null;
  originalClockOutTime: string | null;
  edited: boolean;
  editedAt: string | null;
  editReason: string | null;
  totalHours: number;
  payrollCategory: string;
  status: string;
  notes: string | null;
  holidayName: string | null;
  clockInLatitude: number | null;
  clockInLongitude: number | null;
  clockInAccuracy: number | null;
  clockOutLatitude: number | null;
  clockOutLongitude: number | null;
  clockOutAccuracy: number | null;
  approvalStatus: 'approved' | 'pending' | 'rejected' | 'unsubmitted';
  skipLunchDeduction: boolean;
  /** A supervisor confirmed they asked for the worked-through lunch. */
  lunchWorkedApprovedAt?: string | null;
  lunchWorkedApprovedByName?: string | null;
  /** Employee claimed these hours off their work schedule — no clock record exists. */
  missedClock: boolean;
  missedClockReason: string | null;
}

export interface EmployeeCategoryTotals {
  baseHours: number;
  overtime15x: number;
  overtime20x: number;
  timeInLieu: number;
  personalLeave: number;
  holidayPay: number;
  publicHoliday: number;
  publicHolidayWorked: number;
  timeInLieuTaken: number;
  longServiceLeave: number;
  otherLeave: number;
  totalHours: number;
}

export interface PayCycleEmployee {
  userId: number;
  firstName: string;
  lastName: string;
  username: string;
  entries: PayCycleTimesheetEntry[];
  totals: EmployeeCategoryTotals;
}

export interface AggregateTotals extends EmployeeCategoryTotals {
  totalEmployees: number;
}

export interface PayCycleDashboardData {
  period: PayCyclePeriod;
  employees: PayCycleEmployee[];
  aggregateTotals: AggregateTotals;
}

class PayCycleDashboardService {
  async getDashboardData(params: {
    payFrequency: 'weekly' | 'fortnightly';
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<PayCycleDashboardData>> {
    const queryParams = new URLSearchParams();
    queryParams.append('payFrequency', params.payFrequency);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    return apiService.get(`/timesheet/admin/pay-cycle-dashboard?${queryParams}`);
  }

  async updateEntry(entryId: number, data: {
    payroll_category: string;
    total_hours: number;
    notes: string | null;
  }): Promise<ApiResponse<unknown>> {
    return apiService.put(`/timesheet/admin/${entryId}`, data);
  }

  async deleteEntry(entryId: number, reason: string): Promise<ApiResponse<unknown>> {
    return apiService.delete(`/timesheet/admin/${entryId}`, { reason });
  }
}

export default new PayCycleDashboardService();
